import axios from "axios";
import { API_CONFIG } from "../config/api";
import type { CloudType } from "../types/api";
import { NGROK } from "../../../../APIs";

// ============= Types =============

export interface TenantData {
  tenantName: string;
  [key: string]: any;
}

export interface AwsAccount {
  id: number;
  accountId: string;
  accountName: string;
  email: string;
  organizationId: string;
  tenantName: string;
  status: string;
  accountType: string;
  joinedMethod: string;
  joinedTimestamp: string;
  roleArn: string;
}

export interface AzureSubscription {
  subscriptionName: string;
  azureSubscriptionId: string;
  [key: string]: any;
}

// ============= Tenant API =============

export const fetchTenants = async (): Promise<TenantData[]> => {
  const response = await axios.get(`${NGROK}/api/get-all-domains`, {

  });
  return response.data || [];
};

// ============= Cloud Account APIs =============

export const fetchGcpProjectIds = async (tenantName: string): Promise<string[]> => {
  const response = await axios.get(`${NGROK}/api/gcp/auth/fetchAllProjectIds`, {

    params: { tenantName },
  });
  return response.data || [];
};

export const fetchAwsAccounts = async (tenantName: string): Promise<AwsAccount[]> => {
  const response = await axios.get(`${NGROK}/api/aws/getAllAccounts`, {

    params: { tenantName },
  });
  return response.data || [];
};

export const fetchAzureSubscriptions = async (tenantName: string): Promise<AzureSubscription[]> => {
  const response = await axios.get(`${NGROK}/api/azureResources/v1/all`, {

    params: { tenantName, type: "SUBSCRIPTION" },
  });
  return response.data || [];
};

// ============= Graph Node APIs =============

export const fetchUserNodes = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-user-nodes`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchServiceAccountNodes = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-service-account-nodes`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchResourceNodes = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-resource-nodes`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchAlertNodes = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-alert-nodes`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchRoleNodes = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-role-nodes`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

// ============= Graph Edge APIs =============

export const fetchUserEdges = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-user-edges`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchNhiEdges = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-nhi-edges`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchResourceEdges = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-resource-edges`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchAlertEdges = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}${API_CONFIG.ENDPOINTS.ANOMALY_EDGES}`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

export const fetchQueryResults = async (tenantName: string, cloudId: string, cloudType: CloudType) => {
  const response = await axios.get(`${NGROK}/api/cloud/get-query-results`, {

    params: { tenantName, cloudId, cloudType },
  });
  return response.data || {};
};

// ============= Analytics APIs (User/SA Resolutions & Suggestions) =============

export interface ResolutionsResponse {
  id: number;
  gcpUserEmail: string;
  resolutions: Record<string, Record<string, string[]>>; // roleId -> { resourceName -> [recommendedRoles] }
  timeRangeStart: string;
  timeRangeEnd: string;
  savedAt: string;
  projectId: string;
  tenantName: string;
}

export interface SuggestionsResponse {
  id: number;
  gcpUserEmail: string;
  suggestions: Record<string, string>; // resourceName or "project-level" -> roleId
  timeRangeStart: string;
  timeRangeEnd: string;
  savedAt: string;
  projectId: string;
  tenantName: string;
}

export const fetchUserAnalyticsResolutions = async (
  tenantName: string,
  projectId: string,
  email: string,
  timeRangeCode: number = 1,
): Promise<ResolutionsResponse | null> => {
  try {
    const response = await axios.get(`${NGROK}/api/gcp/analytics/get-user-analytics-resolutions`, {

      params: { tenantName, projectId, email, timeRangeCode },
    });
    return response.data || null;
  } catch (error) {
    console.error("Error fetching user analytics resolutions:", error);
    return null;
  }
};

export const fetchUserAnalyticsSuggestions = async (
  tenantName: string,
  projectId: string,
  email: string,
  timeRangeCode: number = 1,
): Promise<SuggestionsResponse | null> => {
  try {
    const response = await axios.get(`${NGROK}/api/gcp/analytics/get-user-analytics-suggestions`, {

      params: { tenantName, projectId, email, timeRangeCode },
    });
    return response.data || null;
  } catch (error) {
    console.error("Error fetching user analytics suggestions:", error);
    return null;
  }
};

// ============= Correlations & Blast Radius APIs =============

export interface CloudSaasCorrelation {
  id: number;
  tenantName: string;
  cloudAccountId: string;
  cloudType: string;
  severity: string;
  entity: string;
  correlationKey: string;
  status: string;
  occurredAt: string;
  createdAt: string;
  updatedAt: string;
  entityAssociated: string[];
  correlation: Record<string, Record<string, string[]>>;
  info: {
    summary: string;
    timeDeltaSeconds: number;
    saasType: string;
    cloudEventTimeUtc: string;
    saasEventTimeUtc: string;
    cloudLog: any;
    saasLog: any;
    context: any;
  };
}

export interface CloudEntityBlastRadius {
  id: number;
  entity: string;
  entityAssociated: string[];
  blastRadius: Record<string, Record<string, string[]>>;
  info: {
    summary: string;
    whyThisMatters: string;
    whatProvidesAccess: string[];
    secretPivot: {
      secretName: string;
      pivotReason: string;
      secretType: string;
    };
    reachableSaas: {
      repositories: string[];
      account: string;
      saasType: string;
    };
    risk: {
      severity: string;
      reason: string;
    };
    recommendedActions: string[];
    generatedAt: string;
  };
  cloudAccountId: string;
  tenantName: string;
}

export const fetchCorrelations = async (tenantName: string, cloudId: string): Promise<CloudSaasCorrelation[]> => {
  try {
    const response = await axios.get(`${NGROK}/api/cloud/cloud-saas-correlations`, {

      params: { tenantName, cloudId },
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching correlations:", error);
    return [];
  }
};

export const fetchBlastRadius = async (tenantName: string, cloudId: string): Promise<CloudEntityBlastRadius[]> => {
  try {
    const response = await axios.get(`${NGROK}/api/cloud/cloud-entity-blast-radius`, {

      params: { tenantName, cloudId },
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching blast radius:", error);
    return [];
  }
};

// ============= Raw Logs API =============

export interface RawLogEntry {
  id: number;
  cloudType: string;
  cloudResourceType: string;
  logTimestamp: string;
  action: string;
  logType: string;
  status: string | null;
  cloudId: string;
  tenantName: string;
  entityAssociated?: string;
  sourceIp?: string;
}

export const fetchRawLogs = async (
  tenantName: string,
  cloudId: string,
  entity: string,
  cloudType: CloudType,
): Promise<RawLogEntry[]> => {
  try {
    const response = await axios.get(`${NGROK}/api/cloud/get-raw-logs`, {

      params: { tenantName, cloudId, entity, cloudType },
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching raw logs:", error);
    return [];
  }
};
